import { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormData105D01 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  solvente: string
  multifibra: string
  diacetato_dw: string
  algodon_dw: string
  poliamida_dw: string
  poliester_dw: string
  acrilico_dw: string
  lana_dw: string
  cambio_color_dw: string
  triacetato_tv: string
  algodon_tv: string
  poliamida_tv: string
  poliester_tv: string
  acrilico_tv: string
  viscosa_tv: string
  cambio_color_tv: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  autorizo: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: FormData105D01) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DEFAULTS: FormData105D01 = {
  numero_solicitud: '', fecha: '', solvente: 'Percloroetileno', multifibra: 'DW y TV',
  diacetato_dw: '', algodon_dw: '', poliamida_dw: '', poliester_dw: '', acrilico_dw: '', lana_dw: '', cambio_color_dw: '',
  triacetato_tv: '', algodon_tv: '', poliamida_tv: '', poliester_tv: '', acrilico_tv: '', viscosa_tv: '', cambio_color_tv: '',
  observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', autorizo: '',
}

const TD = "border border-gray-500 px-1 py-0.5 align-top text-xs"

const DW_ROWS: [string, keyof FormData105D01][] = [
  ['diacetato', 'diacetato_dw'],
  ['ALGODÓN blanqueado', 'algodon_dw'],
  ['poliamida', 'poliamida_dw'],
  ['poliéster', 'poliester_dw'],
  ['acrílico', 'acrilico_dw'],
  ['LANA', 'lana_dw'],
  ['Cambio de color:', 'cambio_color_dw'],
]

const TV_ROWS: [string, keyof FormData105D01][] = [
  ['triacetato', 'triacetato_tv'],
  ['ALGODÓN blanqueado', 'algodon_tv'],
  ['poliamida', 'poliamida_tv'],
  ['poliéster', 'poliester_tv'],
  ['acrílico', 'acrilico_tv'],
  ['viscosa', 'viscosa_tv'],
  ['Cambio de color:', 'cambio_color_tv'],
]

export function NMXA105D01Form({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol
  const [formData, setFormData] = useState<FormData105D01>({
    ...DEFAULTS,
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({ ...DEFAULTS, realizo: realizoUser || '', ...initialData, mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormData105D01, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return rest
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-105-D01-INNTEX-2020 SOLIDEZ DEL COLOR AL LAVADO EN SECO',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-105-D01? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({ ...formData, ROW_ID: rowId, _EDIT_MODE: true } as any)
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-105-D01?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const showDW = formData.multifibra.includes('DW')
  const showTV = formData.multifibra.includes('TV')

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  const subTable = (title: string, rows: [string, keyof FormData105D01][]) => (
    <table className="w-full text-xs border-collapse border border-gray-500">
      <thead>
        <tr>
          <th colSpan={2} className="border border-gray-500 p-1 text-center font-bold">{title}</th>
        </tr>
        <tr className="bg-gray-50">
          <th className="border border-gray-500 p-1 text-left">Fibra</th>
          <th className="border border-gray-500 p-1 text-center">Transferencia</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, field]) => (
          <tr key={field}>
            <td className="border border-gray-500 p-1">{label}</td>
            <td className="border border-gray-500 p-0.5">
              <Input className="h-5 text-xs px-1 border-0 shadow-none"
                value={formData[field] as string || ''} readOnly={isReadOnly}
                onChange={e => set(field, e.target.value)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            {/* Row 1: Número de solicitud + Fecha */}
            <tr>
              <td colSpan={3} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NUMERO DE SOLICITUD:</span>
                  <Input className={inputCls('flex-1')}
                    value={formData.numero_solicitud} readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)} />
                </div>
              </td>
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">FECHA:</span>
                  <Input type="date" className={inputCls('flex-1')}
                    value={formData.fecha} readOnly={isReadOnly}
                    onChange={e => set('fecha', e.target.value)} />
                </div>
              </td>
            </tr>
            {/* Row 2: Título + MET */}
            <tr>
              <td colSpan={3} className={`${TD} text-center font-bold py-1`}>
                NMX-A-105-D01-INNTEX-2020 SOLIDEZ DEL COLOR AL LAVADO EN SECO
              </td>
              <td className={`${TD} text-center font-bold whitespace-nowrap`}>MET-7.2-22</td>
            </tr>
          </tbody>
        </table>

        {/* Solvente + otro + selector multifibra */}
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="font-medium whitespace-nowrap">Solvente utilizado:</span>
          <Input className={inputCls('w-36')}
            value={formData.solvente} readOnly={isReadOnly}
            onChange={e => set('solvente', e.target.value)} />
          {!isReadOnly && (
            <>
              <span className="font-medium whitespace-nowrap ml-2">Multifibra:</span>
              <Select value={formData.multifibra} onValueChange={v => set('multifibra', v)}>
                <SelectTrigger className="h-6 text-xs w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DW">DW</SelectItem>
                  <SelectItem value="TV">TV</SelectItem>
                  <SelectItem value="DW y TV">DW y TV</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* ── MAIN SECTION: tablas (izq) + Realizó/Autorizó (der) ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              {/* Left: DW and/or TV tables side by side */}
              <td className="border border-gray-500 p-2 align-top">
                <div className={`grid gap-3 ${showDW && showTV ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {showDW && subTable('TELA MULTIFIBRA DW', DW_ROWS)}
                  {showTV && subTable('TELA MULTIFIBRA TV', TV_ROWS)}
                </div>
              </td>
              {/* Right: Realizó / Autorizó */}
              <td className="border border-gray-500 p-2 align-top w-[28%]">
                <div className="space-y-6">
                  <div>
                    <div className="text-xs font-medium mb-1">Realizó:</div>
                    <Input value={formData.realizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1">Autorizó:</div>
                    <Input value={formData.autorizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── FOOTER ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
